import { Fragment, ReactNode, useEffect } from 'react'
import SEO from '../components/SEO'
import SiteNav from '../components/SiteNav'
import Footer from '../components/Footer'
import { OneCmdChip, OneCmdInline } from '../components/OneCmd'

interface Faq { q: string; a: string }

// Wrap any bare http(s) URL in <a>. The raw string is still used for JSON-LD
// (search engines want plain text in FAQ schema), but the visible answer renders
// clickable links — needed for the Supabase docs references in particular.
const URL_PATTERN = /(https?:\/\/[^\s)]+)/g
const TRAILING_PUNCT = /[,.;:!?]+$/
function renderAnswer(text: string): ReactNode {
  // String.split with a capturing group interleaves captures at odd indices.
  const parts = text.split(URL_PATTERN)
  return parts.map((part, i) => {
    if (i % 2 === 0) return <Fragment key={i}>{part}</Fragment>
    const trail = part.match(TRAILING_PUNCT)?.[0] ?? ''
    const url = trail ? part.slice(0, -trail.length) : part
    return (
      <Fragment key={i}>
        <a href={url} target="_blank" rel="noopener noreferrer">
          {url}
        </a>
        {trail}
      </Fragment>
    )
  })
}

const FAQS: Faq[] = [
  {
    q: 'What is the whole deploy process?',
    a: 'Install the OpenDeploy plugin or follow the OpenDeploy docs for your agent, then run "/opendeploy" inside your agent. Every OpenDeploy command starts with /opendeploy. The agent inspects the project, detects the framework, build command, start command, port, env vars, and database needs, creates the deployment, watches the build, and returns a live URL. After the first successful deploy, it also sends a bind link so you can unlock the dashboard.',
  },
  {
    q: 'Do I need to install a plugin first?',
    a: 'The best path is to install the native OpenDeploy plugin or skill for Claude Code, Codex, Cursor, VS Code, OpenClaw, OpenCode, or Hermes. If your agent does not have a native plugin, it can still read the public docs and use the npm CLI. The goal is the same: give the agent the deploy instruction once, then let it run the deployment flow.',
  },
  {
    q: 'Do I need an account before the first deploy?',
    a: 'No. The first deploy can be created by your agent with an OpenDeploy agent token. That lets you test the real running app before signup. If the deploy works, the agent gives you a bind link so you can sign in, bind the project to your account, and keep managing it.',
  },
  {
    q: 'Where is the dashboard?',
    a: 'For the first successful deployment, the dashboard is opened through the bind link your agent returns. The bind link signs you in, binds the agent-created project to your account, and takes you to the project dashboard. The dashboard is observe-only — the agent operates the deployment; the dashboard shows you status, live URL, build and runtime logs, deploy history, vitals, and resources so you can verify what shipped.',
  },
  {
    q: 'What does the agent return after deploy?',
    a: 'If the project is not bound to an account yet, the agent returns the live app URL plus a bind link. If your token is already connected to an account, the agent returns the live app URL plus the project dashboard URL directly.',
  },
  {
    q: 'What can I do in the dashboard after binding the project?',
    a: 'The dashboard is the human observation surface, not an operation console. You verify project status, open the live URL, read build and runtime logs, review deploy history, watch vitals and resource usage, and view your billing. Operations like deploy, redeploy, env-var updates, domain wiring, and rollback are driven by your agent through one command — the dashboard is where you watch what shipped.',
  },
  {
    q: 'What is the price after beta?',
    a: 'As of June 13, 2026, OpenDeploy is free during beta and beta ends on July 1, 2026. Paid GA starts July 1, 2026 at $20 per month on the Builder plan.',
  },
  {
    q: 'What languages and frameworks does OpenDeploy support?',
    a: 'OpenDeploy is general-purpose: 30+ frameworks across Node and TypeScript (Next.js, Nuxt, Remix, SvelteKit, Astro, Express, NestJS), Python (FastAPI, Django, Flask), Go, Rust, Ruby on Rails, PHP / Laravel, Java / Spring Boot, Elixir / Phoenix, and .NET. Static sites work, and any project with a Dockerfile works. The agent inspects the repo, picks the right build and start commands, and deploys.',
  },
  {
    q: 'What databases are included?',
    a: 'Managed PostgreSQL, MySQL, MongoDB, and Redis are available on paid plans. Free beta projects can still use an external database by providing its connection string as an environment variable.',
  },
  {
    q: 'Does OpenDeploy support Supabase?',
    a: 'Yes — you can keep using a Supabase project by providing the Supabase connection details as environment variables on your OpenDeploy app. The same pattern works for any external database or service with a connection string. Useful Supabase links: project API keys at https://supabase.com/dashboard/project/_/settings/api, the Postgres connection string at https://supabase.com/dashboard/project/_/settings/database, and the official environment-variable guide at https://supabase.com/docs/guides/getting-started/local-development#environment-variables.',
  },
  {
    q: 'What is included during beta?',
    a: 'The beta plan is free for one live app and one service per account with an OpenDeploy subdomain, build logs, deploy history, and an app resource profile of 2 vCPU, 4 GB memory, 20 GB storage, and 150 GB monthly bandwidth. Custom domains and managed databases are not included on Free.',
  },
  {
    q: 'What happens if the build fails?',
    a: 'The agent watches the deployment logs and can fix safe, common blockers like missing build commands, wrong ports, or obvious dependency issues. If the failure needs a product decision or a secret value, it should stop and tell you exactly what is missing.',
  },
  {
    q: 'How are secrets and environment variables handled?',
    a: 'The agent should detect required variable names, but you still provide the real secret values. OpenDeploy credentials live in ~/.opendeploy/auth.json on your machine, and deployment API calls use Bearer tokens only against the OpenDeploy gateway.',
  },
]

const SECTIONS: Array<{ n: string; label: string; body: JSX.Element }> = [
  {
    n: '01',
    label: 'Why agent-first',
    body: (
      <>
        Your agent already knows your app inside out: the stack, routes, env
        vars, and database. You shouldn't have to configure deployment by hand.
        OpenDeploy treats <em>your agent as the operator</em>.
      </>
    ),
  },
  {
    n: '02',
    label: 'Why it stays simple',
    body: (
      <>
        <em>Ship without leaving your agent.</em> OpenDeploy is the deploy
        target it can drive end to end. <OneCmdInline>One command</OneCmdInline>,
        and your app goes live and stays live. Free during beta.
      </>
    ),
  },
]

const WHY_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
}

export default function Why() {
  useEffect(() => {
    document.body.classList.add('v2-why-page')
    return () => document.body.classList.remove('v2-why-page')
  }, [])

  return (
    <>
      <SEO
        title="Why OpenDeploy"
        description="Your agent knows your app inside out. OpenDeploy lets it ship and host the app with one command."
        path="/why"
        jsonLd={WHY_JSONLD}
      />

      <div className="od-home od-why-shell">
        <SiteNav />

        <main className="od-why-main">
          <section className="od-why-hero" aria-labelledby="why-title">
            <h1 id="why-title">
              The place gen-apps live.
            </h1>
            <p>
              Your agent already knows your app. <em>1 cmd</em> — agent ships,
              hosts, returns a live URL. Full-stack, database included.
            </p>
            <OneCmdChip />
          </section>

          <section className="od-why-grid" aria-label="Why OpenDeploy">
            {SECTIONS.map((s) => (
              <article key={s.n} className="od-why-card">
                <div className="od-why-card-head">
                  <span className="od-why-card-n">{s.n}</span>
                  <span>{s.label}</span>
                </div>
                <p>{s.body}</p>
              </article>
            ))}
          </section>

          <section className="od-why-stats" aria-labelledby="why-stats-title">
            <header className="od-why-stats-head">
              <h2 id="why-stats-title">The DevOps tax, in real numbers.</h2>
              <p>
                Public surveys. Real sources. What humans usually spend on the
                deploy step, and what an agent on OpenDeploy collapses it to.
              </p>
            </header>

            <ol className="od-why-stats-grid">
              <li className="od-why-stat">
                <p className="od-why-stat-label">
                  Teams that ship a change in under 1 hour
                </p>
                <div className="od-why-stat-row">
                  <div className="od-why-stat-cell od-why-stat-before">
                    <span className="od-why-stat-pill">industry</span>
                    <span className="od-why-stat-num">9.4%</span>
                    <span className="od-why-stat-side">
                      only this many teams complete a code-to-prod change
                      in &lt; 1 hour (DORA 2025)
                    </span>
                  </div>
                  <div className="od-why-stat-cell od-why-stat-after">
                    <span className="od-why-stat-pill">opendeploy</span>
                    <span className="od-why-stat-num">&lt; 5 min</span>
                    <span className="od-why-stat-side">
                      one command — agent reads docs, builds, deploys, returns URL
                    </span>
                  </div>
                </div>
                <p className="od-why-stat-source">
                  Source:{' '}
                  <a
                    href="https://dora.dev/research/2025/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    DORA 2025 Accelerate State of DevOps Report
                  </a>
                </p>
              </li>

              <li className="od-why-stat">
                <p className="od-why-stat-label">
                  Deploy success rate (1 − change failure rate)
                </p>
                <div className="od-why-stat-row">
                  <div className="od-why-stat-cell od-why-stat-before">
                    <span className="od-why-stat-pill">industry</span>
                    <span className="od-why-stat-num">8.5%</span>
                    <span className="od-why-stat-side">
                      of teams hit the ideal 0–2% change failure rate
                      (DORA 2025); elite cluster ≈ 5% CFR
                    </span>
                  </div>
                  <div className="od-why-stat-cell od-why-stat-after">
                    <span className="od-why-stat-pill">opendeploy · target</span>
                    <span className="od-why-stat-num">≥ 95%</span>
                    <span className="od-why-stat-side">
                      DORA elite baseline — agent retries safe blockers,
                      escalates real ones
                    </span>
                  </div>
                </div>
                <p className="od-why-stat-source">
                  Source:{' '}
                  <a
                    href="https://dora.dev/research/2025/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    DORA 2025 Accelerate State of DevOps Report
                  </a>
                </p>
              </li>

              <li className="od-why-stat">
                <p className="od-why-stat-label">
                  Devs frustrated by tech debt &amp; deploy-stack complexity
                </p>
                <div className="od-why-stat-row">
                  <div className="od-why-stat-cell od-why-stat-before">
                    <span className="od-why-stat-pill">human</span>
                    <span className="od-why-stat-num">62%</span>
                    <span className="od-why-stat-side">
                      flag tech debt as a top frustration; complex build /
                      deploy stacks rank #2–#3 (Stack Overflow 2025, 49k+ devs)
                    </span>
                  </div>
                  <div className="od-why-stat-cell od-why-stat-after">
                    <span className="od-why-stat-pill">opendeploy</span>
                    <span className="od-why-stat-num">no stack to learn</span>
                    <span className="od-why-stat-side">
                      one command, no dashboard, no YAML — your agent reads
                      docs, you don't
                    </span>
                  </div>
                </div>
                <p className="od-why-stat-source">
                  Source:{' '}
                  <a
                    href="https://survey.stackoverflow.co/2025/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    2025 Stack Overflow Developer Survey
                  </a>
                </p>
              </li>

              <li className="od-why-stat">
                <p className="od-why-stat-label">
                  Productivity lift when AI runs the outer loop
                </p>
                <div className="od-why-stat-row">
                  <div className="od-why-stat-cell od-why-stat-before">
                    <span className="od-why-stat-pill">github · 2025</span>
                    <span className="od-why-stat-num">+55%</span>
                    <span className="od-why-stat-side">
                      AI tools lift developer productivity; manual deploys
                      &ldquo;more and more rare&rdquo; (Octoverse 2025)
                    </span>
                  </div>
                  <div className="od-why-stat-cell od-why-stat-after">
                    <span className="od-why-stat-pill">opendeploy</span>
                    <span className="od-why-stat-num">agent runs it</span>
                    <span className="od-why-stat-side">
                      build, integrate, deploy, watch logs — handed to your agent
                    </span>
                  </div>
                </div>
                <p className="od-why-stat-source">
                  Source:{' '}
                  <a
                    href="https://github.blog/news-insights/octoverse/what-986-million-code-pushes-say-about-the-developer-workflow-in-2025/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    GitHub Octoverse 2025 — Developer Workflow Report
                  </a>
                </p>
              </li>
            </ol>
          </section>

          <section className="od-why-faq" aria-labelledby="why-faq-title">
            <h2 id="why-faq-title">Frequently asked</h2>
            <div className="od-why-faq-list">
              {FAQS.map((f) => (
                <details key={f.q} className="od-why-faq-item">
                  <summary>{f.q}</summary>
                  <p>{renderAnswer(f.a)}</p>
                </details>
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  )
}
